import { buildAssetPreviewUrl } from '../../utils/mediaUrl';

export interface EmbeddedImageData {
  sourceImageUrl: string;
  cropRect: { x: number; y: number; width: number; height: number };
  imageWidth: number;
  imageHeight: number;
}

export function readEmbeddedImageRecord(metadata: Record<string, unknown> | null | undefined): EmbeddedImageData | null {
  if (!metadata || typeof metadata !== 'object') return null;
  const raw = (metadata['relatedGraphEmbeddedImage'] || metadata['embeddedImage']) as Record<string, unknown> | null | undefined;
  if (!raw || typeof raw !== 'object') return null;
  const sourceImageUrl = typeof raw['sourceImageUrl'] === 'string' ? (raw['sourceImageUrl'] as string).trim() : '';
  if (!sourceImageUrl) return null;
  const rect = raw['cropRect'] as { x?: unknown; y?: unknown; width?: unknown; height?: unknown } | null | undefined;
  const x = Number(rect?.x);
  const y = Number(rect?.y);
  const width = Number(rect?.width);
  const height = Number(rect?.height);
  const imageWidth = Number(raw['imageWidth']);
  const imageHeight = Number(raw['imageHeight']);
  if (![x, y, width, height].every(Number.isFinite) || width <= 0 || height <= 0) return null;
  if (!Number.isFinite(imageWidth) || !Number.isFinite(imageHeight) || imageWidth <= 0 || imageHeight <= 0) return null;
  return { sourceImageUrl, cropRect: { x, y, width, height }, imageWidth, imageHeight };
}

/** 列表项是否可能有嵌入图（含仅有 assetId、URL 待解析的情况） */
export function hasParticleEmbeddedImageHint(metadata: unknown): boolean {
  if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) return false;
  const m = metadata as Record<string, unknown>;
  const raw = (m['relatedGraphEmbeddedImage'] || m['embeddedImage']) as Record<string, unknown> | null | undefined;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return false;
  const rect = raw['cropRect'] as { width?: unknown; height?: unknown } | null | undefined;
  const width = Number(rect?.width);
  const height = Number(rect?.height);
  if (Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0) return true;
  const url = typeof raw['sourceImageUrl'] === 'string' ? raw['sourceImageUrl'].trim() : '';
  const assetId = typeof raw['sourceAssetId'] === 'string' ? raw['sourceAssetId'].trim() : '';
  return Boolean(url || assetId);
}

/** Clamp crop rect to source image bounds (pixel coordinates). */
export function clampEmbeddedCropRect(
  cropRect: EmbeddedImageData['cropRect'],
  imageWidth: number,
  imageHeight: number,
): EmbeddedImageData['cropRect'] {
  const maxW = Math.max(1, Math.round(imageWidth));
  const maxH = Math.max(1, Math.round(imageHeight));
  const x = Math.max(0, Math.min(Math.round(cropRect.x), maxW - 1));
  const y = Math.max(0, Math.min(Math.round(cropRect.y), maxH - 1));
  const width = Math.max(1, Math.min(Math.round(cropRect.width), maxW - x));
  const height = Math.max(1, Math.min(Math.round(cropRect.height), maxH - y));
  return { x, y, width, height };
}

/**
 * OSS image process: crop (+ optional downscale for display target).
 * Pass this into getSignedOssUrl({ process }) so the signature covers the crop.
 */
export function buildOssEmbeddedCropProcess(
  record: Pick<EmbeddedImageData, 'cropRect' | 'imageWidth' | 'imageHeight'>,
  targetPixelWidth?: number,
): string {
  const crop = clampEmbeddedCropRect(record.cropRect, record.imageWidth, record.imageHeight);
  let process = `image/crop,x_${crop.x},y_${crop.y},w_${crop.width},h_${crop.height}`;
  const targetWidth = Math.round(Number(targetPixelWidth) || 0);
  if (targetWidth > 0 && crop.width > targetWidth) {
    process += `/resize,w_${targetWidth}/quality,q_85`;
  } else {
    process += '/quality,q_85';
  }
  return process;
}

export function withOssProcess(rawUrl: string, process: string): string {
  const trimmed = String(rawUrl || '').trim();
  const ossProcess = String(process || '').trim();
  if (!trimmed || !ossProcess) return trimmed;
  try {
    const parsed = new URL(trimmed.startsWith('//') ? `https:${trimmed}` : trimmed);
    parsed.searchParams.set('x-oss-process', ossProcess);
    return parsed.toString();
  } catch {
    const sep = trimmed.includes('?') ? '&' : '?';
    return `${trimmed}${sep}x-oss-process=${encodeURIComponent(ossProcess)}`;
  }
}

/**
 * Build a browser-loadable URL for the embedded crop.
 * Applies OSS server-side crop；签名直链加载（禁止 media proxy）。
 * Prefer getSignedOssUrl(ossId, { process }) when ossId is available.
 */
export function buildEmbeddedImageSrc(
  record: EmbeddedImageData,
  targetPixelWidth?: number,
): string | null {
  const ossProcess = buildOssEmbeddedCropProcess(record, targetPixelWidth);
  const urlWithProcess = withOssProcess(record.sourceImageUrl, ossProcess);
  if (!urlWithProcess) return null;
  return buildAssetPreviewUrl(urlWithProcess) || urlWithProcess;
}
